import { AxiosPromise, AxiosStatic } from "axios";
import {
  Investor,
  MarketingPreferenceType,
  NetPromoterScore,
  UpdateInvestorDto,
  PMFInput,
  PMFResponse,
  WatchlistEntry,
  AddToWatchlistDto,
} from "../types/investor";
import {
  AccreditationQuestion,
  AccreditationRequest,
} from "../types/Accreditation";
import {
  CompleteOnboardingV2Response,
  DealInterestStatusResponse,
  OnboardInvestorReturn,
  OnboardingRulesProgressResponse,
  OnboardingV2ProfileUpdate,
  OnboardingV2ProgressResponse,
  StepListOnboardingRuleSet,
  UpdateOnboardingAPIPayload,
} from "../types/Onboarding";
import { CompleteOnboardingV2Options } from "../types/OnboardingRequests";
import {
  SubmitMandatoryQuestionnaireInput,
  MandatoryQuestionnaireQuestion,
} from "../types/MandatoryQuestionnaire";

export const getInvestor =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<Investor> =>
    await appAxios.get(`${serverURL}/investor`, {
      withCredentials: true,
    });

export const getAccreditationQuestionnaire =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<AccreditationQuestion[]> =>
    await appAxios.get(`${serverURL}/investor/accreditation/questionnaire`, {
      withCredentials: true,
    });

export const reaccreditateInvestor =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: AccreditationRequest): AxiosPromise<AccreditationQuestion[]> =>
    await appAxios.post(`${serverURL}/investor/reaccreditation`, data, {
      withCredentials: true,
    });

export const submitOnboardingAccreditation =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: AccreditationRequest): AxiosPromise<Investor> =>
    await appAxios.post(`${serverURL}/onboarding/accreditation`, data, {
      withCredentials: true,
    });

export const onboardInvestor =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    data: UpdateOnboardingAPIPayload
  ): AxiosPromise<OnboardInvestorReturn> =>
    await appAxios.post(`${serverURL}/investor/onboarding/v2`, data, {
      withCredentials: true,
    });

export const getOnboardingV2Progress =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<OnboardingV2ProgressResponse> =>
    await appAxios.get(`${serverURL}/onboarding/progress`, {
      withCredentials: true,
    });

export const getOnboardingRulesProgress =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    rules: StepListOnboardingRuleSet
  ): AxiosPromise<OnboardingRulesProgressResponse> =>
    await appAxios.get(`${serverURL}/onboarding/progress`, {
      params: { rules },
      withCredentials: true,
    });

export const updateOnboardingV2Profile =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    data: UpdateOnboardingAPIPayload | OnboardingV2ProfileUpdate
  ): AxiosPromise<Investor> =>
    await appAxios.put(`${serverURL}/onboarding/profile`, data, {
      withCredentials: true,
    });

export const completeOnboardingV2 =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    options?: CompleteOnboardingV2Options
  ): AxiosPromise<CompleteOnboardingV2Response> =>
    await appAxios.post(
      `${serverURL}/onboarding/completion`,
      {},
      {
        withCredentials: true,
        ...(options?.rules ? { params: { rules: options.rules } } : {}),
        ...(options?.applicationId
          ? { headers: { "x-application-id": options.applicationId } }
          : {}),
      }
    );

export const getDealInterestStatus =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (dealId: string): AxiosPromise<DealInterestStatusResponse> =>
    await appAxios.get(`${serverURL}/onboarding/deal-interest/${dealId}`, {
      withCredentials: true,
    });

export const acknowledgeRiskWarning =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: { dealId?: string }): AxiosPromise<DealInterestStatusResponse> =>
    await appAxios.post(
      `${serverURL}/onboarding/risk-warning-acknowledgement`,
      data,
      {
        withCredentials: true,
      }
    );

export const updateDealReminder =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (
    dealId: string,
    data: { reminderOptIn: boolean }
  ): AxiosPromise<DealInterestStatusResponse> =>
    await appAxios.put(
      `${serverURL}/onboarding/deal-interest/${dealId}/reminder`,
      data,
      {
        withCredentials: true,
      }
    );

export const postNPS =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (npsData: NetPromoterScore): AxiosPromise<NetPromoterScore> =>
    await appAxios.post(`${serverURL}/investor/nps`, npsData, {
      withCredentials: true,
    });

export const postMarketingPreferences =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: {
    marketingPreference: MarketingPreferenceType;
  }): AxiosPromise<MarketingPreferenceType> =>
    await appAxios.post(`${serverURL}/investor/marketing-preferences`, data, {
      withCredentials: true,
    });

export const getMandatoryQuestionnaire =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<MandatoryQuestionnaireQuestion[]> =>
    await appAxios.get(`${serverURL}/investor/mandatory-questionnaire`, {
      withCredentials: true,
    });

export const submitMandatoryQuestionnaire =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: SubmitMandatoryQuestionnaireInput): AxiosPromise<void> =>
    await appAxios.post(
      `${serverURL}/investor/mandatory-questionnaire-result`,
      data,
      {
        withCredentials: true,
      }
    );

export const submitQuickSurvey =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: { source: string }): AxiosPromise<void> =>
    await appAxios.post(`${serverURL}/investor/quick-survey`, data, {
      withCredentials: true,
    });

export const updateInvestor =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: UpdateInvestorDto): AxiosPromise<Investor> =>
    await appAxios.put(`${serverURL}/investor`, data, {
      withCredentials: true,
    });

export const postPMF =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (pmfData: PMFInput): AxiosPromise<PMFResponse> =>
    await appAxios.post(`${serverURL}/investor/pmf`, pmfData, {
      withCredentials: true,
    });

export const getWatchlist =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<WatchlistEntry[]> =>
    await appAxios.get(`${serverURL}/investor/watchlist`, {
      withCredentials: true,
    });

export const addToWatchlist =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: AddToWatchlistDto): AxiosPromise<WatchlistEntry> =>
    await appAxios.post(`${serverURL}/investor/watchlist`, data, {
      withCredentials: true,
    });

export const removeFromWatchlist =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (dealId: string): AxiosPromise<void> =>
    await appAxios.delete(`${serverURL}/investor/watchlist/${dealId}`, {
      withCredentials: true,
    });
